import { Stack, Box, Typography } from "@mui/material";
import { capitalizeText } from "helpers/methods";
import React from "react";
import editorTokens from "../SignRequestEditor.tokens";
import {
  DragHandleIcon,
  getFieldTypeIcon,
  PropertiesTabIcon,
} from "./EditorIcons";
import { focusRing } from "theme/colors";
import { IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/**
 * `row`   — full-width entry under FIELDS / PERSONAL INFO
 * `quick` — half-width tile under FREQUENTLY USED
 * `icon`  — legacy icon-only chip used by the mobile field strip
 */
type FieldVariant = "row" | "quick" | "icon";

interface FieldProps {
  field: any;
  setSelectedField: (field: string | null) => void;
  setDragStartPosition: (position: { x: number; y: number }) => void;
  dragDistance: React.MutableRefObject<number>;
  showLabel?: boolean;
  variant?: FieldVariant;
  /** Rows under FIELDS carry the six-dot grab affordance; personal info does not. */
  showDragHandle?: boolean;
  /**
   * Keyboard equivalent of the mouse drag-drop: inserts this field type at a
   * default position on the page in view (WCAG 2.1.1). When provided, the tile
   * becomes a real button (Enter/Space activate).
   */
  onKeyboardInsert?: (type: string) => void;
  /** Custom-field rows: edit / delete the saved definition (hover actions). */
  onEdit?: () => void;
  onDelete?: () => void;
}

const { leftRail, palette } = editorTokens;

const Field: React.FC<FieldProps> = ({
  field,
  setDragStartPosition,
  setSelectedField,
  dragDistance,
  showLabel = true,
  variant = showLabel ? "row" : "icon",
  showDragHandle = false,
  onKeyboardInsert,
  onEdit,
  onDelete,
}) => {
  // A library row names itself and borrows its type's glyph (`iconType`); a
  // palette row is named after its type.
  const Glyph = getFieldTypeIcon(field.iconType ?? field.type);
  const label =
    field.name ?? capitalizeText(String(field.type).replace(/_/g, " "));
  const isQuick = variant === "quick";
  const isIcon = variant === "icon";

  const handleGrab = (x: number, y: number) => {
    setSelectedField(field.type);
    setDragStartPosition({ x, y });
    dragDistance.current = 0;
  };

  // Keyboard activation: insert the field without a pointer. Kept separate from
  // the mouse grab handlers so drag behaviour is unchanged.
  const keyboardProps: Record<string, any> = onKeyboardInsert
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": `Add ${label} field`,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onKeyboardInsert(field.type);
          }
        },
      }
    : {};

  const focusRingSx = {
    "&:focus-visible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: "2px",
    },
  };

  if (isIcon) {
    return (
      <Stack
        direction="row"
        {...keyboardProps}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          backgroundColor: palette.white,
          borderRadius: "8px",
          position: "relative",
          width: "48px",
          cursor: "grab",
          userSelect: "none",
          "&.MuiStack-root": { marginLeft: 2.5 },
          ...focusRingSx,
        }}
        onMouseDown={(e) => handleGrab(e.clientX, e.clientY)}
        onTouchStart={(e) => {
          e.stopPropagation();
          const touch = e.touches[0];
          handleGrab(touch.clientX, touch.clientY);
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            width: "48px",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginX: "7.5px",
              border: `1px solid ${palette.borderMain}`,
              color: palette.ink,
            }}
          >
            <Glyph size={20} />
          </Box>
          <Typography
            sx={{
              color: palette.body,
              fontWeight: 500,
              fontSize: "0.75rem",
              textAlign: "center",
              lineHeight: 1.4,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Stack>
    );
  }

  // A custom-field row also carries Edit/Delete buttons. They can't sit inside
  // the row's own "Add field" button (WCAG 4.1.2 nested controls), so the
  // keyboard button wraps only the glyph and label in that case.
  const hasRowActions = Boolean(onEdit || onDelete);

  return (
    <Box
      {...(hasRowActions ? {} : keyboardProps)}
      onMouseDown={(e) => handleGrab(e.clientX, e.clientY)}
      onTouchStart={(e) => {
        e.stopPropagation();
        const touch = e.touches[0];
        handleGrab(touch.clientX, touch.clientY);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: palette.white,
        border: `1px solid ${leftRail.rowBorder}`,
        minWidth: 0,
        borderRadius: isQuick ? leftRail.quickRadius : leftRail.rowRadius,
        p: isQuick ? leftRail.quickPadding : leftRail.rowPadding,
        gap: isQuick ? leftRail.quickInnerGap : leftRail.rowInnerGap,
        cursor: "grab",
        userSelect: "none",
        transition:
          "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
        "&:hover": {
          borderColor: leftRail.rowHoverBorder,
          boxShadow: leftRail.rowHoverShadow,
          transform: "translateY(-1px)",
        },
        "&:active": { cursor: "grabbing" },
        ...focusRingSx,
      }}
    >
      <Box
        {...(hasRowActions ? keyboardProps : {})}
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          gap: isQuick ? leftRail.quickInnerGap : leftRail.rowInnerGap,
          ...(hasRowActions ? focusRingSx : {}),
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",
            flexShrink: 0,
            color: isQuick ? palette.accent : palette.ink,
          }}
        >
          <Glyph
            size={isQuick ? leftRail.quickIconSize : leftRail.rowIconSize}
          />
        </Box>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: isQuick ? leftRail.quickFontSize : leftRail.rowFontSize,
            fontWeight: isQuick
              ? leftRail.quickFontWeight
              : leftRail.rowFontWeight,
            color: palette.ink,
            lineHeight: "normal",
            letterSpacing: "normal",
            // A wrapped label would make the tile taller than its neighbour;
            // every field name fits on one line at the tightened tile metrics.
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
      {onEdit && (
        <Tooltip title="Edit custom field" arrow>
          <IconButton
            size="small"
            aria-label={`Edit ${label}`}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            sx={{ p: 0.25, color: leftRail.handleColor, flexShrink: 0 }}
          >
            <PropertiesTabIcon size={14} />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete custom field" arrow>
          <IconButton
            size="small"
            aria-label={`Delete ${label}`}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ p: 0.25, color: leftRail.handleColor, flexShrink: 0 }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {showDragHandle && (
        <Box
          component="span"
          sx={{ display: "flex", flexShrink: 0, color: leftRail.handleColor }}
        >
          <DragHandleIcon size={leftRail.handleIconSize} />
        </Box>
      )}
    </Box>
  );
};

export default Field;
